import { useMemo, useState } from "react";
import moment, { type Moment } from "moment";
import { DownloadOutlined } from "@ant-design/icons";
import { Button, DatePicker, Space, Table } from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";

import { client } from "@/sdk";
import { formatBalance } from "@/utils/formatters";

const { RangePicker } = DatePicker;

type RangeValue = [Moment | null, Moment | null] | null;

type BalanceUnholdHistoryItem = {
  _id: string;
  createdAt: number;
  amount: number;
  startBalance: number;
  endBalance: number;
  purchaseId?: string | null;
  purchaseTitle?: string | null;
  user?: {
    _id?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    phoneNo?: string | null;
  } | null;
};

type BalanceUnholdHistoriesResponse = {
  getBalanceUnholdHistories: {
    items: BalanceUnholdHistoryItem[];
    totalPages: number;
  };
};

type ExportBalanceUnholdReportResponse = {
  exportBalanceUnholdReport: {
    reportUrl: string;
  };
};

const GET_BALANCE_UNHOLD_HISTORIES = /* GraphQL */ `
  query GetBalanceUnholdHistories($page: Int!, $limit: Int!, $date: DateRange) {
    getBalanceUnholdHistories(page: $page, limit: $limit, date: $date) {
      items {
        _id
        createdAt
        amount
        startBalance
        endBalance
        purchaseId
        purchaseTitle
        user {
          _id
          firstName
          lastName
          phoneNo
        }
      }
      totalPages
    }
  }
`;

const EXPORT_BALANCE_UNHOLD_REPORT = /* GraphQL */ `
  mutation ExportBalanceUnholdReport($startDate: String!, $endDate: String!) {
    exportBalanceUnholdReport(startDate: $startDate, endDate: $endDate) {
      reportUrl
    }
  }
`;

export function HoldReleaseReportsPage() {
  const [page, setPage] = useState(0);
  const [dateRange, setDateRange] = useState<RangeValue>(null);
  const limit = 20;

  const dateFilter = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      return undefined;
    }

    return {
      startDate: dateRange[0].clone().startOf("day").valueOf(),
      endDate: dateRange[1].clone().endOf("day").valueOf(),
    };
  }, [dateRange]);

  const { data, isLoading } = useQuery({
    queryKey: ["reports", "balance-unhold", page, dateFilter?.startDate, dateFilter?.endDate],
    queryFn: async () => {
      const data = await client.request<BalanceUnholdHistoriesResponse>(
        GET_BALANCE_UNHOLD_HISTORIES,
        {
          page,
          limit,
          date: dateFilter,
        }
      );
      return data.getBalanceUnholdHistories;
    },
    keepPreviousData: true,
  });

  const { mutate: exportReport, isLoading: isExporting } = useMutation({
    mutationFn: async () => {
      if (!dateRange?.[0] || !dateRange?.[1]) {
        return;
      }

      const data = await client.request<ExportBalanceUnholdReportResponse>(
        EXPORT_BALANCE_UNHOLD_REPORT,
        {
          startDate: dateRange[0].format("YYYYMMDD"),
          endDate: dateRange[1].format("YYYYMMDD"),
        }
      );

      const { reportUrl } = data.exportBalanceUnholdReport;
      window.open(reportUrl.startsWith("http") ? reportUrl : `/${reportUrl}`);
    },
  });

  return (
    <main className="page">
      <div className="page-header">
        <h3>Битүүмж чөлөөлөлтийн тайлан</h3>
        <Space size={12}>
          <RangePicker
            allowClear
            value={dateRange}
            onChange={(value) => {
              setPage(0);
              setDateRange(value);
            }}
            ranges={{
              "Энэ сар": [moment().startOf("month"), moment().endOf("month")],
              "Энэ долоо хоног": [moment().startOf("week"), moment().endOf("week")],
            }}
          />
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            disabled={!dateRange?.[0] || !dateRange?.[1]}
            loading={isExporting}
            onClick={() => exportReport()}
          >
            Excel тайлан татах
          </Button>
        </Space>
      </div>
      <div className="page-body">
        <div className="page-table">
          <Table
            loading={isLoading}
            dataSource={data?.items ?? []}
            rowKey="_id"
            size="small"
            pagination={{
              current: page + 1,
              pageSize: limit,
              total: (data?.totalPages ?? 1) * limit,
              onChange: (p) => setPage(p - 1),
              showSizeChanger: false,
            }}
            columns={[
              {
                title: "Огноо",
                dataIndex: "createdAt",
                width: 160,
                render: (v: number) =>
                  new Date(v).toISOString().replace("T", " ").slice(0, 19),
              },
              {
                title: "Хэрэглэгч",
                dataIndex: "user",
                width: 180,
                render: (user: BalanceUnholdHistoryItem["user"]) =>
                  user
                    ? `${user.lastName ?? ""} ${user.firstName ?? ""}`.trim() ||
                      user.phoneNo
                    : "",
              },
              {
                title: "Утас",
                dataIndex: "user",
                key: "phone",
                width: 140,
                render: (user: BalanceUnholdHistoryItem["user"]) =>
                  user?.phoneNo ?? "",
              },
              {
                title: "Чөлөөлсөн дүн",
                dataIndex: "amount",
                width: 140,
                render: (v: number) => <span>{formatBalance({ balance: v })} ₮</span>,
              },
              {
                title: "Эхний бэлэн мөнгө",
                dataIndex: "startBalance",
                width: 160,
                render: (v: number) => <span>{formatBalance({ balance: v })} ₮</span>,
              },
              {
                title: "Дараах бэлэн мөнгө",
                dataIndex: "endBalance",
                width: 160,
                render: (v: number) => <span>{formatBalance({ balance: v })} ₮</span>,
              },
              {
                title: "Худалдан авалт",
                dataIndex: "purchaseTitle",
                width: 220,
                render: (v: string | null | undefined) => v || "—",
              },
              {
                title: "Purchase ID",
                dataIndex: "purchaseId",
                ellipsis: true,
                render: (v: string | null | undefined) => v || "—",
              },
            ]}
          />
        </div>
      </div>
    </main>
  );
}
